import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, Package, Truck, CheckCircle, Clock, Loader2, 
  MapPin, ExternalLink, Calendar, FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ordersAPI } from '../utils/api';

const OrderTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(searchParams.get('orderId') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-track if params provided in URL
  useEffect(() => {
    const urlOrderId = searchParams.get('orderId');
    const urlEmail = searchParams.get('email');
    
    if (urlOrderId && urlEmail) {
      setOrderId(urlOrderId);
      setEmail(urlEmail);
      handleTrackWithParams(urlOrderId, urlEmail);
    }
  }, [searchParams]);

  const handleTrackWithParams = async (trackOrderId, trackEmail) => {
    setError('');
    setOrder(null);
    setLoading(true);

    try {
      const data = await ordersAPI.track(trackOrderId, trackEmail);
      setOrder(data);
    } catch (err) {
      setError('Order not found. Please verify your order ID and email.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    handleTrackWithParams(orderId, email);
  };

  const statusSteps = [
    { key: 'pending', label: 'Order Received', icon: Clock, description: 'Order placed and awaiting confirmation' },
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, description: 'Order confirmed and queued for production' },
    { key: 'processing', label: 'In Production', icon: Package, description: 'Your order is being produced' },
    { key: 'shipped', label: 'Shipped', icon: Truck, description: 'On the way to you' },
    { key: 'delivered', label: 'Delivered', icon: MapPin, description: 'Package delivered' },
  ];

  const getStatusIndex = (status) => {
    const index = statusSteps.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-ivory-50" data-testid="order-tracking-page">
      {/* Header */}
      <div className="relative gradient-hero py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center">
              <Package className="w-7 h-7 text-gold-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">
              Track Your Order
            </h1>
          </div>
          <p className="text-ivory-500 font-body max-w-2xl">
            Enter your order number and email to check the status of your order.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Search Form */}
          <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-8 mb-8">
            <form onSubmit={handleTrack} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="orderId" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                    Order Number
                  </label>
                  <input
                    type="text"
                    id="orderId"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="e.g., ORD-20260104-ABC123"
                    className="w-full px-4 py-3 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all font-body"
                    required
                    data-testid="order-id-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ivory-700 mb-2 font-body">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email used for order"
                    className="w-full px-4 py-3 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 placeholder-ivory-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all font-body"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              {error && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-red-600 text-sm font-body">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-3 btn-luxury-primary rounded-lg font-body font-medium"
                data-testid="track-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Searching...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Search className="w-5 h-5" />
                    Track Order
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Order Results */}
          {order && (
            <div className="space-y-6" data-testid="order-results">
              {/* Order Header Card */}
              <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-heading font-semibold text-ivory-800">
                      Order #{order.order_id}
                    </h2>
                    <p className="text-ivory-500 font-body text-sm flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4" />
                      Placed on {formatDate(order.created_at)}
                    </p>
                  </div>
                  <span className={`px-4 py-2 text-sm rounded-full font-body font-medium capitalize ${
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                    order.status === 'confirmed' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                    order.status === 'processing' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                    order.status === 'shipped' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                    order.status === 'delivered' ? 'bg-green-100 text-green-700 border border-green-200' :
                    'bg-ivory-200 text-ivory-600 border border-ivory-300'
                  }`}>
                    {order.status}
                  </span>
                </div>

                {/* Tracking Info (if shipped) */}
                {order.tracking_number && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h3 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Tracking Information
                    </h3>
                    <p className="text-purple-700 font-body">
                      <span className="font-medium">Tracking Number:</span> {order.tracking_number}
                    </p>
                    {order.tracking_url && (
                      <a 
                        href={order.tracking_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 font-body text-sm mt-2"
                      >
                        Track Package <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {order.estimated_delivery_date && (
                      <p className="text-purple-700 font-body text-sm mt-2">
                        <span className="font-medium">Estimated Delivery:</span> {formatDate(order.estimated_delivery_date)}
                      </p>
                    )}
                  </div>
                )}

                {/* Artwork Status */}
                {order.artwork_status && (
                  <div className={`rounded-lg p-4 mb-6 ${
                    order.artwork_status === 'approved' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${
                      order.artwork_status === 'approved' ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      <FileCheck className="w-4 h-4" />
                      Artwork Status: {order.artwork_status === 'approved' ? 'Approved' : 'Pending Review'}
                    </h3>
                    <p className={`text-sm font-body ${
                      order.artwork_status === 'approved' ? 'text-green-700' : 'text-yellow-700'
                    }`}>
                      {order.artwork_status === 'approved' 
                        ? 'Your artwork has been approved and production has started.'
                        : 'Your artwork is being reviewed. Production will begin after approval.'}
                    </p>
                    {order.estimated_ship_date && order.artwork_status === 'approved' && (
                      <p className="text-green-700 font-body text-sm mt-2">
                        <span className="font-medium">Estimated Ship Date:</span> {formatDate(order.estimated_ship_date)}
                      </p>
                    )}
                  </div>
                )}

                {/* Status Timeline */}
                {order.status !== 'cancelled' && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-ivory-700 mb-4 font-body">Order Progress</h3>
                    <div className="flex items-start justify-between relative">
                      {statusSteps.map((step, index) => {
                        const currentIndex = getStatusIndex(order.status);
                        const isActive = index <= currentIndex;
                        const isCurrent = index === currentIndex;
                        
                        return (
                          <div key={step.key} className="flex flex-col items-center relative flex-1">
                            {index > 0 && (
                              <div 
                                className={`absolute left-0 right-1/2 top-5 h-1 -translate-y-1/2 transition-colors ${
                                  isActive ? 'bg-gold-500' : 'bg-ivory-300'
                                }`}
                              />
                            )}
                            {index < statusSteps.length - 1 && (
                              <div 
                                className={`absolute left-1/2 right-0 top-5 h-1 -translate-y-1/2 transition-colors ${
                                  index < currentIndex ? 'bg-gold-500' : 'bg-ivory-300'
                                }`}
                              />
                            )}
                            <div 
                              className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                isActive ? 'bg-gold-500 text-white' : 'bg-ivory-200 text-ivory-400'
                              } ${isCurrent ? 'ring-4 ring-gold-500/30 scale-110' : ''}`}
                            >
                              <step.icon className="w-5 h-5" />
                            </div>
                            <span className={`mt-2 text-xs font-body text-center ${
                              isActive ? 'text-ivory-800 font-medium' : 'text-ivory-400'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Status History */}
                {order.status_history && order.status_history.length > 0 && (
                  <div className="border-t border-ivory-200 pt-6">
                    <h3 className="text-sm font-medium text-ivory-700 mb-4 font-body">Status History</h3>
                    <div className="space-y-3">
                      {order.status_history.slice().reverse().map((entry, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex-shrink-0">
                            <div className={`w-3 h-3 rounded-full mt-1.5 ${
                              index === 0 ? 'bg-gold-500' : 'bg-ivory-300'
                            }`} />
                          </div>
                          <div className="flex-1 pb-3 border-b border-ivory-200 last:border-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                              <span className="font-medium text-ivory-800 font-body capitalize">
                                {entry.status}
                              </span>
                              <span className="text-xs text-ivory-500 font-body">
                                {formatDateTime(entry.timestamp)}
                              </span>
                            </div>
                            {entry.note && (
                              <p className="text-sm text-ivory-600 font-body mt-1">{entry.note}</p>
                            )}
                            {entry.tracking_number && (
                              <p className="text-sm text-purple-600 font-body mt-1">
                                Tracking: {entry.tracking_number}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Items Card */}
              <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-6">
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-4">Order Items</h3>
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-ivory-200 last:border-0">
                      <div className="flex items-center gap-4">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.product_name}
                            className="w-16 h-16 rounded-lg object-cover bg-ivory-200"
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/100x100/f5f3f0/9a8b7d?text=Product';
                            }}
                          />
                        )}
                        <div>
                          <p className="text-ivory-800 font-body font-medium">{item.product_name}</p>
                          <p className="text-ivory-500 font-body text-sm">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <p className="text-gold-600 font-body font-semibold">
                        ${(item.unit_price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Order Total */}
                <div className="border-t border-ivory-200 pt-4 mt-4">
                  <div className="flex justify-between text-xl font-heading font-semibold">
                    <span className="text-ivory-800">Order Total</span>
                    <span className="text-gold-600">${order.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-ivory-500 font-body text-sm">
              Need help with your order?{' '}
              <Link to="/contact" className="text-gold-600 hover:text-gold-700">
                Contact us
              </Link>
              {' '}or call{' '}
              <a href="tel:+18005210527" className="text-gold-600 hover:text-gold-700">
                +1 800 521 0527
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
